import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import cn from 'clsx'

import { mediaQueryUp } from 'components/utils/breakpoint'
import IconDetail from 'components/Common/IconDetail'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferBadge from 'components/Common/OfferBadge'

import config from 'constants/config'
import { take } from 'lib/arrayUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

import OfferTilePricing from '../../Support/OfferTilePricing'
import TileInclusions from '../../Support/OfferTileInclusions'
import SoldOutMessage from '../../Support/SoldOutMessage'
import SearchTileFlightOnlyPricing from '../../Support/SearchTileFlightOnlyPricing'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import GeoContext from 'contexts/geoContext'
import Pane from 'components/Common/Pane'
import { connect } from 'react-redux'
import TextButton from 'components/Luxkit/Button/TextButton'
import { getCustomerType } from 'lib/peopleLikeMeUtils'
import useHotelOfferSales from 'hooks/useHotelOfferSales'
import PeopleLikeMeSalesBanner from '../../../../SearchV2/Components/PeopleLikeMe/PeopleLikeMeSalesBanner'
import ReserveNowTileButton from '../../Support/ReserveNowTileButton'
import { DestinationOfferListAbTestContext } from 'contexts/OfferList/DestinationOfferListAbTestProvider'
import Group from 'components/Luxkit/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { isUserReviewsAbTestEnabled, newOfferTileAndLandingPageEnabled } from 'selectors/featuresSelectors'
import { showUserReviewsRating } from 'lib/reviewUtils'
import { setSearchParamValue } from 'lib/urlUtils'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { queryKeySelectedOfferIds } from 'constants/url'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import { countOccupants } from 'lib/occupancyUtils'
import SearchTileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/SearchTileCancellationPolicy'
import { isDepositEnabledForOfferType, isDepositAvailableOnOffer } from 'lib/depositsUtils'
import OfferTilePropertyHeading from '.././OfferTilePropertyHeading'
import OfferTileLocationSection from '.././OfferTileLocationSection'
import ViewBundleOffer from '.././ViewBundleOffer'

const Main = styled(Pane)`
  display: grid;
  position: relative;
  z-index: 0;

  ${mediaQueryUp.md} {
    grid-template-rows: 1fr;
    grid-template-columns: 40% 1fr;
  }
`

const MainImageSegment = styled.div`
  display: grid;
  gap: ${rem(4)};
  position: relative;

  ${mediaQueryUp.md} {
    grid-template-rows: 1fr;
  }

`

const DetailSegment = styled.div`
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  width: 100%;
  justify-content: space-between;
  display: grid;
  grid-template-columns: 60% 1fr;
  min-height: ${rem(360)};
`

const LeftDetailSegment = styled.div`
  > * + * {
    margin-top: ${rem(8)}
  }
`

const RightDetailSegment = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: fit-content;
  margin-left: ${rem(32)};
  min-height: ${rem(84)};

  &.landing-page-test {
    margin-top: 0;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: flex-end;
  flex-direction: column;
  min-width: ${rem(180)};
  align-items: flex-start;
`

const StyledSearchTileFlightOnlyPricing = styled(SearchTileFlightOnlyPricing)`
  align-items: flex-start;
`

const FlightPackage = styled(IconDetail)`
  margin-top: ${rem(16)};
`

const OfferOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & > * + * {
    margin-top: ${rem(8)};
  }
`

const StyledOfferBadge = styled(OfferBadge)`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
`

const AlignedLoader = styled(LoadingIndicator)`
  align-items: flex-start;
`

const PreSelectCheapestRoom = styled(Group)`
  margin: ${rem(24)} 0;
  padding: ${rem(16)} 0;
  border-width: ${rem(1)} 0px;
  border-style: solid;
  border-color: ${props => props.theme.palette.neutral.five};
`

const StyledReserveNowTileButton = styled(ReserveNowTileButton)`
  margin-top: ${rem(16)};
`

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const ViewBundleOfferWrapper = styled.div`
  margin-top: ${rem(16)};
  grid-column: 1 / 3;
`

interface MappedProps {
  iOS: boolean;
  shouldShowSaleNumbers: boolean;
  windowSearch: string;
  newOfferTileAndLandingPageAbTestEnabled: boolean;
  shouldShowMapButton: boolean;
  isUserReviewsEnabled: boolean;
  offerAvailableRates: App.OfferAvailableRates;
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  hasDates: boolean,
  offerMetaData?: App.OfferListMetaData;
  bestPriceForDates: App.OfferAvailableRate;
  fetchingPriceForDates: boolean
  soldOut: boolean
  filters?: App.OfferListFilters;
  bestPricePackage: App.Package;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  offerLinkIncludesFilters?: boolean;
}

const imageParams: ImageParams = {
  sm: '16:9',
  smWidth: '100vw',
  md: '16:9',
  mdWidth: '67vw',
  lg: '16:9',
  lgWidth: '762px',
  quality: 'good',
}

function NewSearchFlashOfferExpanded(props: Props & MappedProps) {
  const {
    offer,
    hasDates,
    bestPriceForDates,
    soldOut,
    filters,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
    shouldShowSaleNumbers,
    windowSearch,
    newOfferTileAndLandingPageAbTestEnabled,
    shouldShowMapButton,
    isUserReviewsEnabled,
    offerAvailableRates,
    offerMetaData,
    offerLinkIncludesFilters,
  } = props

  const {
    property,
    name,
    offerFlightsEnabled,
  } = offer

  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)

  const destinationOfferContext = useContext(DestinationOfferListAbTestContext)
  const isDestinationPage = destinationOfferContext?.isDestinationPage

  const twoGuestsOneRoom = useMemo(() => {
    if (filters?.rooms && filters.rooms.length === 1) {
      const count = countOccupants(filters.rooms)
      return count.adults === 2 && count.infants === 0 && count.children === 0
    }
    return false
  }, [filters?.rooms])

  const abTestOfferTile = newOfferTileAndLandingPageAbTestEnabled && isDestinationPage && hasDates && twoGuestsOneRoom
  const customerType = useMemo(() => getCustomerType(windowSearch), [windowSearch])

  const [count, loading] = useHotelOfferSales(offer?.id, customerType)

  const { currentRegionHasFlights } = useContext(GeoContext)
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const checkInDate = filters.checkIn ? moment(filters.checkIn) : undefined
  const cancellationPolicyType = bestPricePackage?.roomRate?.cancellationPolicy?.type

  const { searchTargetLabel, offerDistanceFromSearchTarget, searchType } = useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[offer.id]
  const showSearchDistance = searchType === 'landmark' && searchTargetLabel && typeof searchTargetDistance === 'number'

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offer.images, 1, 0) : offer.images

  const priceMultiplier = bestPriceForDates ? 1 : (filters?.rooms?.length || 1)

  const showSecureWithDepositTag = useMemo(() => isDepositEnabledForOfferType(offer.type) &&
  offer.packages.some((pkg) => {
    const propertyFees = pkg?.propertyFees * priceMultiplier || 0
    const packagePrice = pkg?.price
    const total = showTaxExclusivePrices ? packagePrice : packagePrice + propertyFees
    const pkgAvailableRate = offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === pkg.uniqueKey)
    return isDepositAvailableOnOffer(offer, currentCurrency, checkInDate, total, pkg, pkgAvailableRate)
  }), [checkInDate, currentCurrency, offer, offerAvailableRates?.rates, priceMultiplier, showTaxExclusivePrices])

  const mapSearchUrl = `/search/map?${setSearchParamValue(windowSearch, queryKeySelectedOfferIds, encodeOfferIds([offer]))}`

  return (
    <Main>
      <MainImageSegment className={cn({ 'landing-page-test': abTestOfferTile })}>
        <ImageCarousel
          images={images}
          imageParams={imageParams}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />
        <OfferOverlayContainer>
          <OfferPartnershipLabel offer={offer} />
        </OfferOverlayContainer>
        {showBadge && <StyledOfferBadge badge={offer.badge} dimensions={{ width: 86, height: 86 }} />}
      </MainImageSegment>
      <div>
        { shouldShowSaleNumbers && !loading && <PeopleLikeMeSalesBanner customerType={customerType} saleCount={count} />}
        <DetailSegment>
          <TripGuard>
            <FloatingBookmarkContainer>
              <BookmarkButton offer={offer} />
            </FloatingBookmarkContainer>
            <CSSBreakpoint only="sm">
              <FloatingBookmarkContainer>
                <BookmarkButton offer={offer} />
              </FloatingBookmarkContainer>
            </CSSBreakpoint>
            <CSSBreakpoint max="xs">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </CSSBreakpoint>
          </TripGuard>
          <LeftDetailSegment>
            <Group direction="vertical" fullWidth gap={4}>
              <TripGuard>
                <CSSBreakpoint min="md">
                  <HeaderActions>
                    {isUserReviewsEnabled && showUserReviewsRating(offer.property.rating, offer.property.reviewsTotal) && (
                      <NumberRating
                        variant="medium"
                        rating={offer.property.rating}
                        total={offer.property.reviewsTotal}
                        ratingSubtitle={offer.property.reviewsSource === 'google' && 'From online reviews'}
                      />
                    )}
                  </HeaderActions>
                </CSSBreakpoint>
              </TripGuard>
              <OfferUrgencyLabels offer={offer} />
              <OfferTileLocationSection
                location={location}
                showSearchDistance={showSearchDistance}
                shouldShowMapLink={shouldShowMapButton}
                mapSearchUrl={mapSearchUrl}
                searchTargetDistance={searchTargetDistance}
                searchTargetLabel={searchTargetLabel}
              />
            </Group>
            <div>
              <OfferTilePropertyHeading name={property.name} descriptiveName={name} />
            </div>

            {isUserReviewsEnabled && showUserReviewsRating(offer.property.rating, offer.property.reviewsTotal) && (
              <CSSBreakpoint max="sm">
                <NumberRating
                  inlineLabel
                  variant="xs"
                  rating={offer.property.rating}
                  total={offer.property.reviewsTotal}
                  ratingSubtitle={offer.property.reviewsSource === 'google' && 'From online reviews'}
                />
              </CSSBreakpoint>
            )}
            <ProductTypeLabel productType={offer.productType} kind="plain" />
            <SearchTileCancellationPolicy cancellationPolicyType={cancellationPolicyType} checkInDate={checkInDate} timezoneOffset={offer?.property?.timezoneOffset} />
            {(offer.tileInclusions?.length || offer.tileDescription) &&
              <TileInclusions
                abTestOfferTile={abTestOfferTile}
                // content will be used as precaution fallback until migration is finished. TODO OPEX-1008 remove content
                content={offer.tileDescription}
                tileInclusionsList={offer.tileInclusions}
                heading={offer.tileInclusionsHeading}
                maxInclusionsCount={3}
              />
          }
          </LeftDetailSegment>
          {abTestOfferTile && bestPricePackage &&
            <PreSelectCheapestRoom direction="vertical" gap={2}>
              <BodyText variant="small" colour="neutral-two">Recommended room</BodyText>
              <BodyText variant="large" weight="bold" >{bestPricePackage.roomType.name}</BodyText>
            </PreSelectCheapestRoom>}
          <RightDetailSegment className={cn({ 'landing-page-test': abTestOfferTile })}>
            <PriceWrapper>
              <Group fullWidth direction="vertical" verticalAlign="start" horizontalAlign="start" gap={16}>
                {bestPricePackage && !offer.bundledWithFlightsOnly &&
                  <OfferTilePricing
                  offer={offer}
                  rooms={filters.rooms}
                  hasDates={hasDates}
                  checkInDate={checkInDate}
                  pkg={bestPricePackage}
                  pricing={bestPriceForDates}
                  offerUrl={offerUrl}
                  align="left"
                  flexibleMonths={filters.flexibleMonths}
                  flexibleNights={filters.flexibleNights}
                  hasPackageWithDepositsEnabled={showSecureWithDepositTag}
                  />
                }
                {bestPricePackage && offer.bundledWithFlightsOnly &&
                  <StyledSearchTileFlightOnlyPricing
                    offer={offer}
                    rooms={filters.rooms}
                    hasDates={hasDates}
                    pkg={bestPricePackage}
                    pricing={bestPriceForDates}
                    offerUrl={offerUrl}

                  />
                }
                {!abTestOfferTile && <TextButton kind="primary" fit="full-width">View offer</TextButton>}
              </Group>
            </PriceWrapper>
            {soldOut && <SoldOutMessage offer={offer} filters={filters} />}
            {currentRegionHasFlights && offerFlightsEnabled && !offer.bundledWithFlightsOnly &&
              <FlightPackage variant="caption-medium" >
                <LinePlaneFlyIcon size={16} colour="primary" />
                Hotel + flight package available
              </FlightPackage>
            }
            {!bestPricePackage && <AlignedLoader opaque floating/>}
            {abTestOfferTile && bestPricePackage &&
              <StyledReserveNowTileButton bestPricePackage={bestPricePackage} filters={filters}/>
            }
          </RightDetailSegment>
          {config.ENABLE_BUNDLE_AND_SAVE && offerMetaData?.bundleOfferId && (
            <ViewBundleOfferWrapper>
              <ViewBundleOffer offer={offer} filters={filters} offerMetaData={offerMetaData} offerLinkIncludesFilters={offerLinkIncludesFilters} />
            </ViewBundleOfferWrapper>
          )}
        </DetailSegment>
      </div>
    </Main>
  )
}

const mapStateToProps = (state: App.State, ownProps: Props): MappedProps => ({
  iOS: state.config.userAgent.osName === 'iOS',
  shouldShowSaleNumbers: state.router.location.pathname.endsWith('people-like-me'),
  windowSearch: state.router.location.search,
  newOfferTileAndLandingPageAbTestEnabled: newOfferTileAndLandingPageEnabled(state),
  shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
  isUserReviewsEnabled: isUserReviewsAbTestEnabled(state),
  offerAvailableRates: state.offer.offerAvailableRates[ownProps.offer.id],
})

export default connect(mapStateToProps)(NewSearchFlashOfferExpanded)
